import React, { useCallback, useEffect, useRef, useState } from "react";
import useAuth from "../../hooks/useAuth";
import ActionButtons from "../ui/popups/ActionButtons";
import { getPortalUrl } from "../../services/api";
import {
  goToProviderPortal,
  hasChosenPatientApp,
  hasPendingInvitation,
  INVITATION_TOKEN_CHANGED_EVENT,
  rememberPatientAppChoice,
} from "../../services/staffPortal";

export const STAFF_PROMPT_TITLE = "Provider Portal";
export const STAFF_PROMPT_MESSAGE = "You also have access to the Arc Connect Provider Portal";
export const GO_TO_PORTAL_LABEL = "Go to Provider Portal";
export const STAY_LABEL = "Stay in Patient App";

/**
 * StaffPortalPrompt — the ONE choice a provider-staff account is offered (owner ruling
 * 2026-09-28, R-753). It replaces R-734's forced web redirect and blocking native notice:
 * staff choose, they are not forced out of the patient app.
 *
 *   - "Go to Provider Portal": web, a full-page navigation to `getPortalUrl()`; native, the
 *     system browser (the portal has no native app).
 *   - "Stay in Patient App" (or closing the pop-up): remembered for the session, so it is not
 *     asked again until the next sign-in.
 *
 * Not shown while:
 *   - the account is not staff (or capabilities could not be read);
 *   - "Stay" was chosen this session, or the page came from the portal's "Switch to Patient
 *     App" (`?from=portal`, recorded as the same choice);
 *   - a data-share invite is pending (owner ruling 1: invites finish first). It re-checks when
 *     the invite is accepted, declined or cleared (INVITATION_TOKEN_CHANGED_EVENT);
 *   - ConsentGate is up (consents outstanding), so two modals never stack.
 *
 * Uses the app's standard ActionButtons pop-up, like InvitedNotification.
 */
const StaffPortalPrompt: React.FC = () => {
  const { user, isStaff, consentsOutstanding } = useAuth();
  const [open, setOpen] = useState(false);
  // Mirrors `open` synchronously: IonModal reports EVERY dismissal through onClose, including
  // the ones this component makes itself, and only a person's dismissal means "Stay".
  const openRef = useRef(false);

  const show = (value: boolean) => {
    openRef.current = value;
    setOpen(value);
  };

  // Only the latest check may open or close the pop-up (the reads are async).
  const checkSeq = useRef(0);

  const eligible = !!user && isStaff && (consentsOutstanding?.length ?? 0) === 0;

  const evaluate = useCallback(async () => {
    const seq = ++checkSeq.current;
    if (!eligible) {
      show(false);
      return;
    }
    const [chosen, invitePending] = await Promise.all([
      hasChosenPatientApp(),
      hasPendingInvitation(),
    ]);
    if (seq === checkSeq.current) show(!chosen && !invitePending);
  }, [eligible]);

  useEffect(() => {
    void evaluate();
    const onInviteChanged = () => void evaluate();
    window.addEventListener(INVITATION_TOKEN_CHANGED_EVENT, onInviteChanged);
    return () => window.removeEventListener(INVITATION_TOKEN_CHANGED_EVENT, onInviteChanged);
  }, [evaluate, user?.profile_id]);

  const stay = async () => {
    if (!openRef.current) return;
    show(false);
    await rememberPatientAppChoice();
  };

  const go = async () => {
    // Native returns to the app afterwards: do not ask again this session.
    await rememberPatientAppChoice();
    show(false);
    await goToProviderPortal(getPortalUrl());
  };

  if (!open) return null;

  return (
    <ActionButtons
      isOpen={open}
      onClose={() => void stay()}
      title={STAFF_PROMPT_TITLE}
      message={STAFF_PROMPT_MESSAGE}
      actionButtons={[
        { text: GO_TO_PORTAL_LABEL, action: go, isPrimary: true },
        { text: STAY_LABEL, action: stay, isPrimary: false },
      ]}
    />
  );
};

export default StaffPortalPrompt;
